import React from 'react';
import { Container, Typography, Paper, Box, Grid, Divider } from '@mui/material';
import { Announcement as AnnouncementIcon } from '@mui/icons-material';

const NewsCard = ({ date, title, content }) => (
  <Paper 
    elevation={2}
    sx={{ 
      p: 3,
      height: '100%',
      backgroundColor: '#ffffff',
      borderRadius: 2,
      transition: 'transform 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)'
      }
    }}
  >
    <Typography variant="caption" color="text.secondary">
      {date}
    </Typography>
    <Typography 
      variant="h6" 
      sx={{ 
        my: 1,
        color: '#1565c0',
        fontWeight: 500
      }}
    >
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {content}
    </Typography>
  </Paper>
);

const HomePage = () => {
  const companyNews = [
    {
      date: 'March 15, 2024',
      title: 'System Update: Enhanced Risk Assessment Model',
      content: 'We are excited to announce the launch of our enhanced risk assessment model, featuring improved accuracy in fraud detection and risk evaluation.'
    },
    {
      date: 'March 10, 2024',
      title: 'New Partnership with Leading Insurance Providers',
      content: 'UW Compass has established strategic partnerships with major insurance providers to streamline the underwriting process across North America.'
    },
    {
      date: 'March 1, 2024',
      title: 'Digital Transformation Success Story',
      content: 'Our platform has helped reduce policy processing time by 60% for our clients, setting new industry standards for efficiency.'
    }
  ];

  return (
    <Container maxWidth="xl">
      {/* Welcome Section */}
      <Box sx={{ 
        pt: 8,
        pb: 6,
        textAlign: 'center'
      }}>
        <Typography 
          variant="h2" 
          sx={{ 
            fontWeight: 600,
            color: '#1565c0',
            mb: 3
          }}
        >
          Welcome to UW Compass
        </Typography>
        
        <Typography 
          variant="h5"
          sx={{ 
            color: '#5c6bc0',
            maxWidth: 800,
            mx: 'auto',
            lineHeight: 1.6,
            mb: 6
          }}
        >
          Empowering underwriters with intelligent solutions for precise risk assessment and efficient policy management
        </Typography>
      </Box>

      {/* Latest Updates Section */}
      <Box sx={{ mb: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: 4 
        }}>
          <AnnouncementIcon sx={{ color: '#1976d2', mr: 2, fontSize: 28 }} />
          <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 500 }}>
            Latest Updates
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {companyNews.map((news, index) => (
            <Grid item xs={12} md={4} key={index}>
              <NewsCard {...news} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Platform Overview */}
      <Box sx={{ 
        backgroundColor: '#f8f9fa',
        borderRadius: 4,
        p: 6,
        mb: 8
      }}>
        <Typography 
          variant="h4" 
          sx={{ 
            color: '#1976d2',
            fontWeight: 500,
            mb: 4
          }}
        >
          About Our Platform
        </Typography>
        
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ mb: 2, color: '#2d3748' }}>
              Our Mission
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
              To revolutionize the insurance underwriting process through innovative technology, 
              making it more efficient, accurate, and accessible for all stakeholders.
            </Typography>
            
            <Typography variant="h6" sx={{ mb: 2, color: '#2d3748' }}>
              Key Benefits
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              • Automated risk assessment with 99.9% accuracy<br />
              • Real-time collaboration between brokers and underwriters<br />
              • Comprehensive audit trail and compliance reporting<br />
              • Advanced analytics and performance insights
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ mb: 2, color: '#2d3748' }}>
              Recent Achievements
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" sx={{ mb: 1, color: 'text.secondary' }}>
                ✓ Processed over 1 million policies in 2023
              </Typography>
              <Typography variant="body1" sx={{ mb: 1, color: 'text.secondary' }}>
                ✓ Reduced average processing time to under 2 hours
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                ✓ Achieved 98% customer satisfaction rate
              </Typography>
            </Box>
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
              "UW Compass has transformed our underwriting process, enabling us to make faster,
              more accurate decisions while maintaining high quality standards."
            </Typography>
            <Typography variant="subtitle2" sx={{ mt: 1, color: '#1976d2' }}>
              - Insurance Industry Leader
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default HomePage; 